import { useCallback, useEffect, useMemo } from 'react'
import { PageHeader } from '@atrvd/saas-ui'
import { hasPermission } from '@dcp/shared'
import {
  KpiCards,
  BankingFilters,
  BankTransactionsTable,
  // DCP-331: kept commented out, not deleted — the chart is coming back.
  // FacilityCategoryChart,
} from '@/components/banking'
import {
  useBankingKpi,
  useFacilities,
  useBankAccounts,
  useUpdateBankTransactionsCategory,
  useDailyBankingTransactions,
  type DailyBankingTransactionsQuery,
  // DCP-331: kept commented out, not deleted — the chart is coming back.
  // useFacilityCategoryData,
} from '@/hooks/useBanking'
import { usePlaidAccounts, useBankTransactionCount, useBankTransactionCategories } from '@/hooks/usePlaid'
import { useCategories } from '@/hooks/useProjections'
import { useBankingFilterParams } from '@/hooks/useBankingFilterParams'
import { lastPageOf } from '@/lib/pagination'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useProfile } from '@/hooks/useProfile'
import { DAILY_BANKING_EXCLUDED_ACCOUNT_TYPES } from '@/constants/banking'
import { pageOrCountError, tableQueryState } from '@/components/common/queryErrorDisplay'
import { isApiTransactionSource, NO_CATEGORY_FILTER_VALUE, NO_CATEGORY_LABEL } from '@/types/banking'
import type {
  AssignableCategory,
  BankTransaction,
  BankAccountRow,
  BankingFilters as BankingFiltersType,
} from '@/types/banking'
import type { PlaidTransaction, PlaidAccount } from '@/lib/supabase'

/**
 * DCP-384: single source of truth for "is this bank account in scope for the
 * selected facilities". Used both by the Account filter options memo and by
 * the facility-change handler that prunes a stale account selection, so the
 * rule can't drift between the two call sites.
 *
 * When no facility is selected, every account (including unassigned ones) is
 * in scope — matches the pre-DCP-384 behavior of the Account dropdown.
 *
 * When one or more facilities are selected, only accounts whose facility_id
 * is one of them are in scope. Accounts with facility_id === null are
 * deliberately EXCLUDED here: the ticket asks for "only bank accounts of the
 * facility selected", and an unassigned account isn't one of them. Flip this
 * exclusion if product later wants unassigned accounts to always show.
 */
function isAccountInFacilityScope(account: BankAccountRow, facilityIds: string[] | undefined): boolean {
  if (!facilityIds || facilityIds.length === 0) return true
  return account.facility_id !== null && facilityIds.includes(account.facility_id)
}

/**
 * DCP-400: an account is offered in the Daily Banking "Account" filter iff it is
 * in facility scope AND its type is not excluded from Daily Banking (credit
 * cards). Offering a card would always yield zero rows because the queries
 * drop card transactions server-side; a bookmarked `?accountIds=<card>` would
 * otherwise reproduce the "zero rows, no visible chip" failure DCP-384 fixed.
 */
function isAccountOfferedOnDailyBanking(account: BankAccountRow, facilityIds: string[] | undefined): boolean {
  if (!isAccountInFacilityScope(account, facilityIds)) return false
  return !(DAILY_BANKING_EXCLUDED_ACCOUNT_TYPES as readonly string[]).includes(account.account_type ?? '')
}

/**
 * Daily Banking page (named "Banking Dashboard" until DCP-341; the route is
 * still `/banking/dashboard` and the file keeps its historical name).
 * Displays KPIs and transactions table.
 *
 * DCP-331: the "Transaction by Facility by Category" bar chart is hidden
 * temporarily and is expected back, so every piece of its wiring below is
 * commented out in place rather than deleted. Nothing outside this file was
 * touched — FacilityCategoryChart, useFacilityCategoryData, their test suites,
 * the facilityCategories query keys and the get_facility_category_chart RPC all
 * stay in place and covered. Restoring the chart means uncommenting the four
 * marked blocks (import, import, data hook, chart glue) and swapping the JSX
 * back to the commented two-column grid at the bottom of this file.
 * BEFORE restoring, mind DCP-400: the table below hides credit-card
 * transactions (see DAILY_BANKING_EXCLUDED_ACCOUNT_TYPES) but the chart's
 * `get_facility_category_chart` RPC has no account-type predicate, so the RPC
 * needs a matching `ba.account_type <> 'credit_card'` migration first.
 */
export function BankingDashboardPage() {
  const { filters, setFilters, clearFilters, page, setPage, pageSize, setPageSize, sort, setSort } =
    useBankingFilterParams()
  // DCP-123: only for selectionResetKey below — the data hooks read the
  // organization themselves.
  const { currentOrganization } = useOrganization()

  // DCP-292 (#3): the "Account" filter must not offer accounts of DEACTIVATED
  // facilities — opt into active-only (unassigned/NULL-facility accounts kept).
  //
  // DCP-384: pulled up here (was previously fetched further down, next to the
  // other data hooks) because `effectiveFilters` below needs both `bankAccounts`
  // and `bankAccountsLoading` before `transactionFiltersBase`/`categoryFilters`/
  // `useBankingKpi` are computed.
  const { data: bankAccounts = [], isLoading: bankAccountsLoading } = useBankAccounts({ activeOnly: true })

  // DCP-384 (Finding 1): reconciles `filters.accountIds` against the CURRENT
  // facility scope, independent of the interactive prune in handleFiltersChange
  // below. That handler only runs when BankingFilters calls onFiltersChange — it
  // never runs on first mount from a bookmarked/shared URL (e.g.
  // ?facilityIds=fac-1&accountIds=acc-b1 where acc-b1 belongs to fac-2), nor
  // when bankAccounts/facilities refetch in the background (e.g. another user
  // deactivates a facility while this tab is open). In both cases accountOptions
  // already stops offering the stale id, but without this memo the data queries
  // below would keep filtering by it — the exact "zero rows, no visible chip"
  // failure DCP-384 exists to prevent.
  //
  // Deliberately a derived value, not a useEffect + setFilters: an effect would
  // rewrite the URL and could fight the user (e.g. mid-selection); a derived
  // value fixes the actual harm — the queries — with no extra render cycle.
  const inScopeAccountIds = useMemo(() => {
    if (!filters.accountIds?.length) return undefined
    // Same ambiguity as Finding 3 in handleFiltersChange: while the account
    // list hasn't loaded yet, we can't tell "out of scope" from "not loaded
    // yet" — pass the ids through unchanged rather than wrongly wipe them.
    if (bankAccountsLoading) return filters.accountIds

    const reconciled = filters.accountIds.filter((id) => {
      const acc = bankAccounts.find((a) => a.id === id)
      // DCP-400: also drop credit_card accounts — see isAccountOfferedOnDailyBanking.
      return acc ? isAccountOfferedOnDailyBanking(acc, filters.facilityIds) : false
    })
    // "All Accounts" shape (see BankingFilters.tsx onChange for accountIds) is
    // `undefined`, not an empty array — match it when nothing survives.
    return reconciled.length > 0 ? reconciled : undefined
  }, [filters.accountIds, filters.facilityIds, bankAccounts, bankAccountsLoading])

  // DCP-384 (Finding 1): the reconciled filters fed to every DATA-fetching
  // query (transactions, counts, categories, KPI) below. Deliberately NOT
  // passed to <BankingFilters> — see the comment at that call site.
  const effectiveFilters = useMemo(
    () => ({ ...filters, accountIds: inScopeAccountIds }),
    [filters, inScopeAccountIds]
  )

  // Prepare bank transaction filters from UI filters
  const transactionFiltersBase = useMemo(() => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
    facilityIds: filters.facilityIds,
    // DCP-384 (Finding 1): reconciled against facility scope, not raw —
    // see effectiveFilters above.
    accountIds: effectiveFilters.accountIds,
    categories: filters.categories,
  }), [filters.startDate, filters.endDate, filters.facilityIds, effectiveFilters.accountIds, filters.categories])

  // Filters with pagination for table display.
  // DCP-123 (AC#6): the server-side sort rides along here and on the export
  // query below — but NOT on `transactionFiltersBase`, which also keys the
  // pagination COUNT: the count does not depend on the order, and keying it on
  // the sort would refetch it on every header click.
  const transactionFiltersWithPagination = useMemo<DailyBankingTransactionsQuery>(() => ({
    ...transactionFiltersBase,
    sort,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  }), [transactionFiltersBase, sort, page, pageSize])

  // DCP-123: the unpaginated CSV-export read, in the ON-SCREEN order so the
  // exported file matches what the user sorted.
  const transactionFiltersForExport = useMemo<DailyBankingTransactionsQuery>(() => ({
    ...transactionFiltersBase,
    sort,
  }), [transactionFiltersBase, sort])

  // DCP-123: the table clears its row selection whenever this changes. Keyed on
  // the reconciled DATA filters only — selection survives paging, page size and
  // sorting (the same rows, merely reordered or on another page), but a filter change
  // drops it so a bulk action can never write rows the user can no longer see.
  // The organization is part of the key as a second guard: DashboardLayout
  // remounts the page on an org switch, and the other org's transaction ids
  // must not stay selected even if that ever changes.
  // JSON of a memoized object built in a fixed key order, so it is stable.
  const organizationId = currentOrganization?.id
  const selectionResetKey = useMemo(
    () => JSON.stringify([organizationId ?? null, transactionFiltersBase]),
    [organizationId, transactionFiltersBase]
  )

  // Fetch dynamic category options — filtered by date/facility only (not by
  // selected categories). DCP-384: no `accountIds` field here, so there's
  // nothing for the Finding 1 reconciliation to route through — this query
  // was never affected by a stale account id.
  const categoryFilters = useMemo(() => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
    facilityIds: filters.facilityIds,
  }), [filters.startDate, filters.endDate, filters.facilityIds])
  const { data: usedCategoryOptions = [] } = useBankTransactionCategories(categoryFilters)
  // DCP-123 (AC#5): "No Category" is always offered, and first — it is the
  // option the user reaches for to find the rows that still need a category,
  // so it must not depend on whether the current range happens to hold one.
  const categoryOptions = useMemo(
    () => [{ value: NO_CATEGORY_FILTER_VALUE, label: NO_CATEGORY_LABEL }, ...usedCategoryOptions],
    [usedCategoryOptions]
  )

  // DCP-123: the categories a row can be ASSIGNED — every active category of
  // the tenant (the same unpaginated read the Manual Transaction dialog uses),
  // not only the ones already in use like the filter options above. Inactive
  // ones are withheld: a retired category must not gain new rows. Sorted by
  // name so the dropdown reads alphabetically regardless of the read's order.
  const { data: tenantCategories } = useCategories()
  const assignableCategories = useMemo<AssignableCategory[]>(
    () =>
      (tenantCategories ?? [])
        .filter((c) => c.is_active)
        .map((c) => ({ id: c.id, name: c.name, type: c.type }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [tenantCategories]
  )

  // DCP-123 (AC#2/#3): the write behind the inline dropdown and the bulk
  // action. The hook applies the change optimistically and toasts failures;
  // the table awaits the promise to decide on its own feedback.
  const { mutateAsync: updateTransactionsCategory } = useUpdateBankTransactionsCategory()

  // DCP-123 RBAC: changing a category is a banking edit, so it needs
  // `manage_banking` (accounts_payable, admin). `treasury` and `view_only` open
  // this page with `view_banking` only and keep the read-only table. Same
  // helper `RequirePermission` and LocAvailabilityPage (`manage_loc`) use; no
  // profile yet → no permission, so the edit affordances never flash in.
  const { data: profile } = useProfile()
  const canCategorize = hasPermission(profile?.productRole, 'manage_banking')
  const handleAssignCategory = useCallback(
    // Resolves with the rows actually written, which the bulk toast reports.
    (ids: string[], category: AssignableCategory): Promise<number> =>
      updateTransactionsCategory({ ids, category }),
    [updateTransactionsCategory]
  )

  // Fetch data
  // DCP-384 (Finding 1): effectiveFilters, not raw filters — the KPI query
  // must not keep filtering by an accountId that fell out of facility scope.
  const { data: kpi, isLoading: kpiLoading } = useBankingKpi(effectiveFilters)
  // DCP-331: commented out, not deleted. Keeping this call alive would fire the
  // org-wide get_facility_category_chart aggregate on every dashboard mount and
  // every date-filter change for a component nobody can see.
  // const { data: chartData, isLoading: chartLoading } = useFacilityCategoryData(filters.startDate, filters.endDate)
  const { data: facilities = [] } = useFacilities()

  // Fetch bank transactions with filters, sort and pagination for table.
  // DCP-123: the typed Daily Banking read (it carries `sort`), on the same
  // cache key as usePlaid's `useBankTransactions` — see the hook's note.
  const transactionsQuery = useDailyBankingTransactions(transactionFiltersWithPagination)
  const bankTransactions = transactionsQuery.data
  const { data: plaidAccounts = [] } = usePlaidAccounts()

  // Build account options for the filter dropdown from bank_accounts ONLY
  // (DCP-218): the re-pointed KPI balance source and transaction filter now key
  // on bank_account_id, so a selected account value must be a bank_account_id.
  // Plaid is abandoned, so plaid_accounts no longer seed the filter. Labels
  // mirror the table: real name + full account number (see mapTransactions,
  // which renders bank_accounts.name + account_number verbatim without masking
  // for this internal AP tool).
  // DCP-384: scope the offered options to the selected facilities via
  // isAccountInFacilityScope (see its docstring for the unassigned-account
  // exclusion rule). filters.facilityIds is a new dependency here — the
  // memo previously never read the facility filter at all, which is the
  // root cause of the bug (Account dropdown always listed every account).
  // DCP-400: also drop credit_card accounts — see isAccountOfferedOnDailyBanking.
  const accountOptions = useMemo(() => {
    const byValue = new Map<string, { value: string; label: string }>()
    for (const acc of bankAccounts) {
      if (!isAccountOfferedOnDailyBanking(acc, filters.facilityIds)) continue
      if (byValue.has(acc.id)) continue
      const label = acc.account_number ? `${acc.name} (${acc.account_number})` : acc.name
      byValue.set(acc.id, { value: acc.id, label })
    }
    return [...byValue.values()]
  }, [bankAccounts, filters.facilityIds])
  const {
    data: totalCount = 0,
    isSuccess: totalCountSettled,
    isFetching: totalCountFetching,
    error: totalCountError,
    refetch: refetchTotalCount,
  } = useBankTransactionCount(transactionFiltersBase)

  // DCP-123: pull `page` back inside the result set when it shrinks under it.
  // With the "No Category" filter, categorizing the last row of the last page
  // removes that page — without this the URL keeps pointing past the end and
  // the table shows its empty state although earlier pages still hold rows.
  // Only acts on a SETTLED, fresh count for the current filters: while the
  // count is loading or refetching (stale data may be smaller than the truth,
  // e.g. an old cached count for a URL opened on a later page), or has failed
  // (`totalCount` is then the 0 default), the URL is left alone. A genuinely
  // empty result clamps to page 1, never 0.
  // An effect rather than a render-time adjustment (as useTablePagination
  // does): the page lives in the URL, and navigating during render is not allowed.
  const lastPage = lastPageOf(totalCount, pageSize)
  useEffect(() => {
    if (!totalCountSettled || totalCountFetching) return
    if (page > lastPage) setPage(lastPage)
  }, [totalCountSettled, totalCountFetching, page, lastPage, setPage])

  // Fetch ALL filtered transactions (without pagination) for CSV export, in
  // the on-screen sort — same typed read as the table above.
  const { data: allBankTransactionsRaw } = useDailyBankingTransactions(transactionFiltersForExport)

  // DCP-331: chart-only glue, commented out rather than deleted. Both symbols
  // exist solely to drive FacilityCategoryChart — click a bar to filter by that
  // facility, and highlight the currently filtered one. No capability is lost
  // while the chart is hidden: the "All Facilities" multi-select in
  // BankingFilters is an independent producer of filters.facilityIds.
  //
  // /** Handle facility click on bar chart — toggle facility in multi-select filter */
  // /** Click bar → set filter to that single facility; click again → clear */
  // const handleFacilityChartClick = useCallback(
  //   (facilityName: string) => {
  //     const facility = facilities.find((f) => f.name === facilityName)
  //     if (!facility) return
  //
  //     const isAlreadySelected = filters.facilityIds?.length === 1 && filters.facilityIds[0] === facility.id
  //     setFilters({ ...filters, facilityIds: isAlreadySelected ? undefined : [facility.id] })
  //   },
  //   [facilities, filters, setFilters]
  // )
  //
  // /** Resolve selected facility names from current filter IDs for chart highlight */
  // const selectedFacilityName = useMemo(() => {
  //   if (!filters.facilityIds?.length) return undefined
  //   // For chart highlight, use first selected facility
  //   return facilities.find((f) => f.id === filters.facilityIds![0])?.name
  // }, [facilities, filters.facilityIds])

  /**
   * Maps transactions to BankTransaction format for table display.
   *
   * DCP-223: previously read Plaid-only fields (`plaid_accounts.name`,
   * `plaid_accounts.mask`, `tx.plaid_category`) — which fell back to
   * "Unknown Account" / "****" / "OTHER" for every manual / file-import
   * row, because those source paths never populate Plaid fields. The
   * server-side query now joins on `bank_accounts` + `categories`, so
   * we read the real account name + account_number's last 4 digits +
   * category name regardless of source.
   *
   * Sign convention for `amount`:
   *   - Amounts are stored at their magnitude without sign normalization;
   *     the category `type` (inflow/outflow) is the source of truth for the
   *     displayed sign, applied at the render layer via formatCategoryAmount
   *     (DCP-124). We forward `categoryType` for that.
   *   - The stored sign only matters as a fallback for uncategorized rows.
   *     For Plaid rows positive = outflow in the raw row (Plaid SDK
   *     convention), so we invert here to our display convention
   *     (positive = inflow); manual / file rows pass through verbatim.
   *     `plaid_transaction_id` is the marker that survives the schema
   *     change (set only for Plaid-sourced rows).
   *
   * Every row is mapped — no client-side dropping. The table is
   * server-paginated/counted (see useBankTransactionCount), so filtering rows
   * out here would desync the visible rows from the page count and break
   * pagination. DCP-324 moved the only rule that DOES drop rows — hide the
   * transactions of accounts in a deactivated facility or in none at all — into
   * the query itself (`applyTransactionFacilityScope`), where `list` and
   * `count` apply it identically and the two cannot disagree.
   */
  const mapTransactions = useCallback(
    (txList: PlaidTransaction[]): BankTransaction[] => {
      return txList
        .map((tx: PlaidTransaction) => {
          const isPlaidTransaction = !!tx.plaid_transaction_id

          // Joined bank account → real name + last 4 digits of account number.
          // Falls back to the Plaid-account lookup only for Plaid rows that
          // pre-date the bank_account_id linkage migration.
          const joinedBankAccount = tx.bank_accounts
          const fallbackPlaidAccount = isPlaidTransaction
            ? plaidAccounts?.find((acc: PlaidAccount) => acc.id === tx.plaid_account_id)
            : null

          // Resolved name without the "Unknown Account" fallback — used both
          // as the rendered account label (with the fallback applied at the
          // last moment) AND as a description fallback when the row itself
          // has no description text. Mixing the fallback string into the
          // description column would double-print "Unknown Account" in the
          // table, which a regression test guards against.
          const resolvedAccountName =
            joinedBankAccount?.name ?? fallbackPlaidAccount?.name ?? null
          const accountNumberRaw =
            joinedBankAccount?.account_number ?? fallbackPlaidAccount?.mask ?? null

          const accountName = resolvedAccountName ?? 'Unknown Account'
          // Display rule (DCP-223): show the full account number from the
          // linked bank_account verbatim. We deliberately don't mask /
          // truncate to last-4 — the operator-facing Banking Dashboard is
          // an internal AP tool, and partial numbers caused more
          // "which account is this?" pings than they prevented.
          // Missing number → em dash, so an empty cell reads as "no value"
          // rather than looking like a rendering glitch.
          const accountNumber = accountNumberRaw ?? '—'

          // User-defined category name from the joined `categories` row.
          // Empty category_id resolves to "No Category" per the Linear spec.
          const category = tx.categories?.name ?? NO_CATEGORY_LABEL

          // DCP-324: facility name straight off the row's ACCOUNT. It used to
          // be looked up in the already-loaded `facilities` list by the
          // transaction's own `facility_id` — a denormalized copy stamped at
          // INSERT that no reassignment ever refreshed, so a reassigned
          // account's history read "Unassigned" (or its previous facility)
          // forever. The query now joins through `bank_accounts`, so the name
          // is always the account's CURRENT one and needs no client lookup.
          //
          // The fallback stays for the LOC drill-down and CSV paths, which
          // share this row type through a LEFT embed; the dashboard's own query
          // joins with `!inner`, so a row reaching here always has a facility.
          const facilityName = tx.bank_accounts?.facilities?.name ?? 'Unassigned'

          return {
            id: tx.id,
            facility: facilityName,
            date: tx.transaction_date,
            accountNumber,
            accountName,
            category,
            // DCP-123: the raw FK behind the inline Category dropdown and the
            // "needs a category" highlight. Deliberately NOT derived from the
            // label above: a legacy Plaid row can carry a `plaid_category` but
            // no `category_id`, and it is uncategorized for this purpose.
            categoryId: tx.category_id ?? null,
            // Category type drives the displayed sign/colour downstream
            // (formatCategoryAmount in BankTransactionsTable), matching the
            // Transactions Import table. Null → the table falls back to the
            // stored sign of the `amount` passed below.
            categoryType: tx.categories?.type ?? null,
            // Amount passed at its stored magnitude. The Plaid raw sign is
            // flipped to our display convention so uncategorized Plaid rows
            // (no category type → sign fallback) still read correctly;
            // categorized rows ignore this sign (abs by category type).
            amount: isPlaidTransaction ? -tx.amount : tx.amount,
            // Don't fall back to the account name here — it already has its
            // own column, so repeating it in Description reads as a glitch.
            description: tx.description || 'Transaction',
            // No source writes a memo for any transaction (bank feed, file
            // import or manual), so the column stays empty. The category has
            // its own column and must not be repeated here.
            memo: '',
            // DCP-355: the BANK's pending flag, not our ingest status. Only
            // bank-feed rows can carry it; manual entries and file imports have
            // no such state, so absence means false rather than unknown.
            pending: tx.pending === true,
            // API Status (DCP-344): only bank-feed rows have an API to report
            // on. Manual entries and file imports get `null` → the table renders
            // "-". Gating on `source` rather than `sync_status` is deliberate:
            // `sync_status` stays at its `'pending'` DEFAULT forever on those
            // rows (no write path ever advances it), which is what used to make
            // them read as a genuine "Pending" sync.
            apiStatus: isApiTransactionSource(tx.source)
              ? tx.sync_status ?? 'pending'
              : null,
          }
        })
    },
    // `facilities` is gone from the deps: DCP-324 made the facility name come
    // off the row's own account embed, so this mapper no longer reads the
    // facility list at all. It is still fetched above for the filter bar.
    [plaidAccounts]
  )

  // Map paginated transactions for table display
  const transactions = useMemo<BankTransaction[]>(() => {
    if (!bankTransactions || bankTransactions.length === 0) {
      return []
    }
    return mapTransactions(bankTransactions)
  }, [bankTransactions, mapTransactions])
  const transactionsState = tableQueryState(
    {
      isPending: transactionsQuery.isPending,
      isPlaceholderData: transactionsQuery.isPlaceholderData,
      isFetching: transactionsQuery.isFetching || totalCountFetching,
      error: pageOrCountError(transactionsQuery.error, totalCountError),
      refetch: () => {
        void transactionsQuery.refetch()
        void refetchTotalCount()
      },
    },
    transactions.length,
  )

  // Map ALL filtered transactions for CSV export (without pagination)
  const allTransactions = useMemo<BankTransaction[]>(() => {
    if (!allBankTransactionsRaw || allBankTransactionsRaw.length === 0) {
      return []
    }
    return mapTransactions(allBankTransactionsRaw)
  }, [allBankTransactionsRaw, mapTransactions])

  /**
   * DCP-384: wraps setFilters so a facility-selection change also prunes any
   * account selection that no longer belongs to the new facility scope.
   * Without this, a previously selected account from a facility that just
   * got deselected stays in filters.accountIds (and in the URL, per
   * useBankingFilterParams) — the Account dropdown would silently drop it
   * from its options while the transactions table kept filtering on it,
   * showing zero rows with no visible chip explaining why.
   *
   * Precedent: ManualTransactionFormDialog's facility onValueChange handler
   * (DCP-125) clears the bank + account selections outright on facility
   * change. This is the read-only-filters equivalent: instead of a blanket
   * clear, we only prune the ids that fell out of scope (using the same
   * isAccountOfferedOnDailyBanking predicate as the options memo above —
   * facility scope plus the DCP-400 account-type exclusion), keeping a
   * still-valid multi-select choice intact when the user merely adds
   * another facility to the selection.
   *
   * DCP-384 (Finding 3): if the user changes the Facility selection before
   * useBankAccounts has resolved, bankAccounts is still `[]` and
   * `bankAccounts.find(...)` matches nothing for every id — not because
   * they're out of scope, but because there's nothing loaded to check them
   * against. That would wrongly collapse a perfectly valid accountIds
   * selection to `undefined`. We can't distinguish "not in scope" from "not
   * loaded yet" here, so we skip the prune entirely while loading and let
   * newFilters through unchanged (the derived inScopeAccountIds memo above
   * still keeps the data queries correct once bankAccounts arrives).
   */
  const handleFiltersChange = useCallback(
    (newFilters: BankingFiltersType) => {
      // Compare as sets, not arrays: the MultiSelect can reorder its value
      // array on toggle, and that reordering alone must NOT be treated as a
      // facility-scope change (it would needlessly re-run the prune below).
      const facilityIdsChanged =
        JSON.stringify([...(newFilters.facilityIds ?? [])].sort()) !==
        JSON.stringify([...(filters.facilityIds ?? [])].sort())

      if (!facilityIdsChanged || !newFilters.accountIds?.length || bankAccountsLoading) {
        setFilters(newFilters)
        return
      }

      const prunedAccountIds = newFilters.accountIds.filter((id) => {
        const acc = bankAccounts.find((a) => a.id === id)
        // An account id that no longer resolves to a loaded bank account is
        // dropped too — it can't be valid under any facility scope.
        // DCP-400: same predicate as accountOptions, so a stale card id from a
        // bookmarked URL is pruned here as well, not only in inScopeAccountIds.
        return acc ? isAccountOfferedOnDailyBanking(acc, newFilters.facilityIds) : false
      })

      setFilters({
        ...newFilters,
        // "All Accounts" shape (see BankingFilters.tsx onChange for accountIds)
        // is `undefined`, not an empty array — match it when nothing survives.
        accountIds: prunedAccountIds.length > 0 ? prunedAccountIds : undefined,
      })
    },
    [filters.facilityIds, bankAccounts, bankAccountsLoading, setFilters]
  )

  return (
    <div className="space-y-6">
      {/* DCP-341: renamed from "Banking Dashboard". The description is
          unchanged — it still describes what the page does. */}
      <PageHeader
        title="Daily Banking"
        description="View and manage your banking transactions"
      />

      {/* Filters */}
      {/* DCP-384 (Finding 1): the RECONCILED filters, not the raw URL state.
          MultiSelect resolves a value's label as `optionMap.get(v) || v` and
          only shows its placeholder on an EMPTY value, so a stale accountId
          that no longer resolves to an option does not fall back to "All
          Accounts" — it paints as a bare UUID with an active clear button,
          and its row isn't in the popover either, so it can't be unchecked.
          Meanwhile the queries below already drop it. Passing
          `effectiveFilters` collapses the value to `[]` (real placeholder)
          and, since this control's onChange spreads the prop, also stops the
          pruned id being re-emitted into the URL on the next interaction. */}
      <BankingFilters
        filters={effectiveFilters}
        onFiltersChange={handleFiltersChange}
        facilities={facilities}
        accountOptions={accountOptions}
        transactions={allTransactions}
        categoryOptions={categoryOptions}
      />

      <KpiCards kpi={kpi} loading={kpiLoading} />

      {/*
       * DCP-331: the original two-column layout, preserved verbatim so restoring
       * the chart is "uncomment this block, delete the standalone table below,
       * uncomment the four marked blocks above". The wrapper grid is commented
       * out along with the chart because a grid holding a single child is dead
       * scaffolding — the page's own space-y-6 gives the table the same 24px
       * rhythm at full width.
       *
       * <div className="grid gap-6 lg:grid-cols-10">
       *   <div className="lg:col-span-3">
       *     <FacilityCategoryChart
       *       data={chartData?.rows ?? []}
       *       categoryKeys={chartData?.categoryKeys ?? []}
       *       signedTotals={chartData?.signedTotals}
       *       selectedFacility={selectedFacilityName}
       *       onFacilityClick={handleFacilityChartClick}
       *       loading={chartLoading}
       *     />
       *   </div>
       *
       *   <div className="lg:col-span-7">
       *     ...the table below, unchanged...
       *   </div>
       * </div>
       */}

      {/* Transactions Table — full width while the chart is hidden (DCP-331) */}
      <BankTransactionsTable
        transactions={transactions}
        {...transactionsState}
        onClearFilters={clearFilters}
        pagination={{ page, pageSize, totalItems: totalCount, onPageChange: setPage, onPageSizeChange: setPageSize }}
        categories={assignableCategories}
        onAssignCategory={handleAssignCategory}
        sort={sort}
        onSortChange={setSort}
        selectionResetKey={selectionResetKey}
        canCategorize={canCategorize}
      />
    </div>
  )
}